import React, { useState } from "react";
import { Button, Select, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from "./CoachForm.module.scss";
import FFKLogo from "../../assets/images/ffk-logo-sm.png";
import useFetch from "../../hooks/useFetch";

const { Option } = Select;

interface Course {
  id: string;
  name: string;
  startDateOfApplication: string;
  endDateOfApplication: string;
}

interface CoachStepOneProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setCourseId: (id: string) => void;
}

const CoachStepOne: React.FC<CoachStepOneProps> = ({ setCurrentStep, setCourseId }) => {
  const { t } = useTranslation();
  const { data: courses, loading, error: fetchError } = useFetch<Course[]>("/courses");
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const noCoursesAvailable = !loading && !fetchError && (!courses || courses.length === 0);

  const handleContinue = () => {
    if (!selectedCourseId) {
      setError(true);
      return;
    }
    setCourseId(selectedCourseId);
    setCurrentStep(1);
  };

  return (
    <div className={styles.StepOneContainer}>
      <div style={{marginBottom: "24px"}}>
        <img src={FFKLogo} alt="FFK Logo"/>
        <h1>{t("app.coaches.stepOne.title")}</h1>
        <p>{t("app.coaches.stepOne.p1")}</p>
        <br/>
        <p>{t("app.coaches.stepOne.p2")}</p>
        <br/>
        <p>
          <ul>
            <li>{t("app.coaches.stepOne.list.uefa")}</li>
            <li>{t("app.coaches.stepOne.list.gk")}</li>
            <li>{t("app.coaches.stepOne.list.fitness")}</li>
            <li>{t("app.coaches.stepOne.list.futsal")}</li>
          </ul>
        </p>
      </div>

      <div className={styles.CourseSelection}>
        <p className={styles.CourseLabel}>
          {t("app.coaches.stepOne.selectCourse")}{" "}
          <Tooltip title={t("app.coaches.stepOne.selectCourseTooltip")}>
            <InfoCircleOutlined className={styles.InfoIcon} />
          </Tooltip>
        </p>
        <Select
            placeholder={t("app.coaches.stepOne.selectCoursePlaceholder")}
            style={{width: "100%", marginBottom: "1rem"}}
            loading={loading}
            disabled={loading || !!fetchError || noCoursesAvailable}
            onChange={(value: string) => {
              setSelectedCourseId(value);
              setError(false);
            }}
        >
          {courses?.map((course) => (
              <Option key={course.id} value={course.id}>
                <span className={styles.CourseName}>{course.name}</span>
                <span className={styles.CourseDates}>
                &nbsp;({course.startDateOfApplication} — {course.endDateOfApplication})
              </span>
              </Option>
          ))}
        </Select>
        {error && (
            <div className={styles.CourseError}>
              <p>{t("app.coaches.stepOne.selectCourseError")}</p>
            </div>
        )}
        {fetchError && (
            <div className={styles.CourseError}>
              <p>{t("app.coaches.stepOne.fetchError")}</p>
          </div>
        )}
        {noCoursesAvailable && (
            <div className={styles.CourseError}>
              <p>{t("app.coaches.stepOne.noCourses")}</p>
            </div>
        )}
      </div>

      <div className={styles.ButtonAlignment}>
        <Button type="primary" className={styles.ButtonStyle} onClick={handleContinue}>
          {t("app.coaches.stepOne.continue")}
        </Button>
      </div>
    </div>
  );
};

export default CoachStepOne;
